import { useState, useEffect, useMemo, useRef } from 'react';
import './BFloatingStatus.css';

// Phase timing estimates (seconds)
const PHASE_TIMING = {
  starting: { duration: 2, cumulative: 2 },
  context: { duration: 12, cumulative: 14 },
  searching: { duration: 20, cumulative: 34 },
  discovery: { duration: 20, cumulative: 34 },
  deep_research: { duration: 15, cumulative: 49 },
  report: { duration: 45, cumulative: 94 },
} as const;

const TOTAL_ESTIMATED_TIME = 94;

// Rotating sayings to lighten the mood
const QUIPS = [
  'Digging through the crates...',
  'Asking the cool record store guy...',
  'Checking the liner notes...',
  'Flipping through the vinyl...',
  'Reading old zine reviews...',
  'Dusting off the turntable...',
  'Rewinding the cassette...',
  'Adjusting the tracking...',
  'Consulting the experts...',
  'Cross-referencing discographies...',
  'Following the rabbit hole...',
  'Tracing the family tree...',
  'Mapping the scene...',
  'Connecting the dots...',
  'Finding the hidden gems...',
  'Unearthing the deep cuts...',
  'Checking who played bass...',
  'Following up on rumors...',
  'Asking around the scene...',
  'This might take a minute...',
];

// Get a random quip, avoiding repeats
function getRandomQuip(lastIndex: number): { quip: string; index: number } {
  let index = Math.floor(Math.random() * QUIPS.length);
  // Avoid immediate repeats
  if (index === lastIndex && QUIPS.length > 1) {
    index = (index + 1) % QUIPS.length;
  }
  return { quip: QUIPS[index], index };
}

interface BFloatingStatusProps {
  phase: string;
  message: string;
  isActive: boolean;
  startTime?: number;
}

export function BFloatingStatus({ phase, isActive, startTime }: BFloatingStatusProps) {
  const lastQuipIndex = useRef(-1);
  const [quip, setQuip] = useState(() => {
    const { quip, index } = getRandomQuip(-1);
    lastQuipIndex.current = index;
    return quip;
  });
  const [elapsed, setElapsed] = useState(0);

  // Track elapsed time
  useEffect(() => {
    if (!isActive || !startTime) return;

    const updateElapsed = () => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [isActive, startTime]);

  // Rotate quip every 4 seconds
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      const { quip, index } = getRandomQuip(lastQuipIndex.current);
      lastQuipIndex.current = index;
      setQuip(quip);
    }, 4000);
    return () => clearInterval(interval);
  }, [isActive]);

  // Calculate progress
  const progress = useMemo(() => {
    // For healing phase, show progress based on elapsed time (estimate ~30s)
    if (phase === 'healing') {
      const healingProgress = Math.min((elapsed / 30) * 100, 95);
      return healingProgress;
    }

    const timing = PHASE_TIMING[phase as keyof typeof PHASE_TIMING];
    if (!timing) return 0;

    const baseProgress = ((timing.cumulative - timing.duration) / TOTAL_ESTIMATED_TIME) * 100;
    const phaseStart = timing.cumulative - timing.duration;
    const timeInPhase = Math.max(0, elapsed - phaseStart);
    const phaseProgress = Math.min(timeInPhase / timing.duration, 0.95);
    const withinPhaseProgress = (phaseProgress * timing.duration / TOTAL_ESTIMATED_TIME) * 100;

    return Math.min(baseProgress + withinPhaseProgress, 98);
  }, [phase, elapsed]);

  if (!isActive || phase === 'complete' || phase === 'cached') return null;

  // SVG circle progress params
  const circleRadius = 14;
  const circleCircumference = 2 * Math.PI * circleRadius;
  const strokeDashoffset = circleCircumference - (progress / 100) * circleCircumference;

  return (
    <div className="b-floating-status">
      <div className="b-floating-status__content">
        <div className="b-floating-status__progress-circle">
          <svg viewBox="0 0 36 36" className="b-floating-status__circle-svg">
            <circle
              className="b-floating-status__circle-bg"
              cx="18"
              cy="18"
              r={circleRadius}
              fill="none"
              strokeWidth="3"
            />
            <circle
              className="b-floating-status__circle-fill"
              cx="18"
              cy="18"
              r={circleRadius}
              fill="none"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={circleCircumference}
              strokeDashoffset={strokeDashoffset}
              transform="rotate(-90 18 18)"
            />
          </svg>
        </div>
        <span className="b-floating-status__quip">{quip}</span>
        <span className="b-floating-status__percent">{Math.round(progress)}%</span>
      </div>
      <div className="b-floating-status__bar">
        <div
          className="b-floating-status__bar-fill"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
